import { useState, useCallback, useRef, useEffect } from 'react';
import useSWR from 'swr';
import { AssessmentAPI } from '@/lib/api/assessments';
import type { AssessmentType, AssessmentSavePayload } from '@/types/assessments';
import { useAuth } from '@/contexts/AuthContext';

export function useAssessment(type: AssessmentType) {
  const { status, user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const saveTimeoutRef = useRef<NodeJS.Timeout>();

  const {
    data,
    error,
    mutate,
    isLoading,
  } = useSWR(
    status === 'signedIn' && user ? [`/api/assessments/${type}`, type] : null,
    () => AssessmentAPI.get(type),
    {
      revalidateOnFocus: false,
      errorRetryCount: 1,
    }
  );

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetch = useCallback(() => {
    mutate();
  }, [mutate]);

  const saveNow = useCallback(async (payload: AssessmentSavePayload) => {
    if (!user) throw new Error('Not authenticated');
    
    setIsSaving(true);
    try {
      const result = await AssessmentAPI.save(type, payload);
      mutate(result, false);
      return result;
    } catch (error) {
      // Retry once on 5xx errors
      if (error instanceof Error && error.message.includes('50')) {
        try {
          const result = await AssessmentAPI.save(type, payload);
          mutate(result, false);
          return result;
        } catch (retryError) {
          throw retryError;
        }
      }
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [type, user, mutate]);

  const save = useCallback((payload: AssessmentSavePayload) => {
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save by 500ms
    saveTimeoutRef.current = setTimeout(() => {
      saveNow(payload).catch((error) => {
        console.error('Auto-save failed:', error);
      });
    }, 500);
  }, [saveNow]);

  const complete = useCallback(async () => {
    return saveNow({ completed: true });
  }, [saveNow]);

  const getValue = useCallback((path: string) => {
    if (!data?.answers) return undefined;
    
    const keys = path.split('.');
    let value = data.answers;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return undefined;
      }
    }
    
    return value;
  }, [data]);

  return {
    data,
    isLoading,
    isSaving,
    error,
    isOnline,
    fetch,
    save,
    saveNow,
    complete,
    getValue,
  };
}